import React, { useEffect, useMemo, useState } from "react";
import { RootState } from "us.helper/types";
import { ConnectedProps, connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { $Button, $Popover, $Input, $MessageBox } from "us.common/components";
import * as Actions from "actions";
import "./Home.scss";
import { Formik } from "formik";
import { TextEditorSchema } from "./Validations";
import { GetTextData } from "./Repository";
import { useLocation } from "react-router";
import { getUrlParams } from "components/EditorHome/Functions";
import { getTemplateGroupName } from "functions";
import { Parameters } from "components";
import DOMPurify from "dompurify";
import { handleTableCellsBorderStyling } from "components/TemplateEditor/Components/RichTextEditor/functions";
import { RTE_CONFIG_TEXT_EDITOR } from "constants/RTEditor";
import { purifyTextContent } from "functions/TemplateEditor";
import { getPurifiedTextContent } from "components/TemplateEditor/Functions";

interface ITextEditorProps {
  onClose: () => void;
}

/**
 * @description - Text editor component.
 * @link Design Document - https://unicorn-solutions.atlassian.net/l/cp/e59ify4K
 * @author Lashini Ayesha <lashinia@unicorn-solutions.com>
 * @since 12/09/2023
 */
const TextEditor: React.FC<ITextEditorProps & PropsFromRedux> = ({
  texts,
  drawer,
  templates,
  onClose,
  saveText,
}) => {
  const { search } = useLocation();
  const { groupId } = getUrlParams(search);
  const [richTextEditor, setRichTextEditor] = useState<any>(null);
  const [saveAsVisible, setSaveAsVisible] = useState<boolean>(false);
  const { t } = useTranslation();
  const { content } = drawer ?? {};
  const textList = texts.data ?? [];

  const groupName = useMemo(() => {
    return getTemplateGroupName(templates.groups, groupId);
  }, [groupId, templates.groups]);

  useEffect(() => {
    if (typeof window.initRTEditor == "function" && !richTextEditor) {
      const editor: any = window.initRTEditor("rte_content", RTE_CONFIG_TEXT_EDITOR);
      setRichTextEditor(editor);
      if (editor != null && content) {
        const purifiedContent = getPurifiedTextContent(content.text);
        editor.setHTMLCode(purifiedContent);
        editor.commitBookmark();
        editor.focus();
        editor.attachEvent(
          "exec_command_bordernone",
          function (state: { returnValue: boolean }, _cmd: any, _value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("bordernone", editor);
          }
        );
        editor.attachEvent(
          "exec_command_bordertop",
          function (state: { returnValue: boolean }, _cmd: any, _value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("bordertop", editor);
          }
        );
        editor.attachEvent(
          "exec_command_borderbottom",
          function (state: { returnValue: boolean }, _cmd: any, _value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("borderbottom", editor);
          }
        );
        editor.attachEvent(
          "exec_command_borderhorizontal",
          function (state: { returnValue: boolean }, _cmd: any, _value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("borderhorizontal", editor);
          }
        );
        editor.attachEvent(
          "exec_command_borderall",
          function (state: { returnValue: boolean }, _cmd: any, _value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("borderall", editor);
          }
        );
        editor.attachEvent(
          "exec_command_borderdashed",
          function (state: { returnValue: boolean }, _cmd: any, _value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("borderstyle", editor, "dashed");
          }
        );
        editor.attachEvent(
          "exec_command_borderdotted",
          function (state: { returnValue: boolean }, _cmd: any, _value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("borderstyle", editor, "dotted");
          }
        );
        editor.attachEvent(
          "exec_command_bordersolid",
          function (state: { returnValue: boolean }, _cmd: any, _value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("borderstyle", editor);
          }
        );
        // attached an event to sanitize the paste content from Word
        editor.attachEvent("change", function () {
          var content = editor.getHTMLCode();
          if (content.includes("xml") && content.includes("Microsoft Word")) {
            const config = {
              FORBID_TAGS: ["style", "xml", "meta"],
              KEEP_CONTENT: false,
              ADD_TAGS: ["params"],
              ADD_ATTR: ["contenteditable"],
            };
            const cleanedContent = DOMPurify.sanitize(content, config);
            editor.setHTMLCode(cleanedContent);
            editor.commitBookmark();
          }
        });
      }
    }
  }, []);

  /**
   * @description - Handle parameter binding with section by double clicking the param, if it's a normal table it will convert to a
   * repeating table if user add a repeating param to a table row
   * @param {string} tokenName - Token name
   * @param {string} _isRepeating - Token is a repeating token or not
   * @param {string} _adapterId - Adapter id
   */
  const handleParamsBinding = (
    tokenName: string,
    _isRepeating: boolean,
    _adapterId?: number
  ) => {
    try {
      if (richTextEditor && typeof richTextEditor.insertHTML == "function") {
        richTextEditor.insertHTML(
          `<params contenteditable="false" suppresscontenteditablewarning="true">${tokenName}</params>`
        );
        richTextEditor.collapse(false);
      }
    } catch (error) {
      console.error("Error in handleParamsBinding function : ", error);
    }
  };

  /**
   * @description - Handle save the changes to selected section
   */
  const handleSave = (values: any) => {
    try {
      // get the updated html content from the editor
      const htmlContent = richTextEditor.getHTMLCode();
      const updatedHtmlContent = purifyTextContent(htmlContent);
      // update the template section
      const request = GetTextData.call({
        updatedHtmlContent,
        content,
        values,
        groupName,
      });
      saveText && saveText({ request: request, texts: textList });
    } catch (error) {
      console.error("Error saving the Section Editor", error);
      $MessageBox(
        "error",
        `US.USC.TEMPLATEEDITOR:MESSAGES.TEXT_UPDATE_FAILED`,
        "",
        ""
      );
    } finally {
      onClose();
    }
  };

  return (
    <>
      <Formik
        initialValues={{
          textName: content?.textName,
        }}
        validationSchema={TextEditorSchema}
        enableReinitialize
        onSubmit={handleSave}
      >
        {({ handleSubmit }: any) => (
          <>
            <div className="section-editor-layout">
              <div className="section-editor-layout-content">
                <div id="rte_content" />
              </div>
              <div className="section-editor-layout-side">
                <Parameters
                  enableRepeatingTab={false}
                  showAllRepeatingAdapters={false}
                  disabledAddRepeatingAdapter={true}
                  isDisplayInTextEditor={true}
                  onDoubleClick={handleParamsBinding}
                />
              </div>
            </div>
            <div className="drawer-footer-fixed align-content-center justify-content-end">
              <div>
                <$Button
                  onClick={
                    content.id == ""
                      ? () => setSaveAsVisible(true)
                      : handleSubmit
                  }
                  htmlType="submit"
                  type="primary"
                >
                  {t("US.COLLECTION.COMMON:COMMON.SAVE")}
                </$Button>
                <$Popover
                  trigger="click"
                  placement="topRight"
                  content={
                    <div className="te-save-as-popover">
                      <div className="mb-3">
                        <$Input
                          required
                          size="small"
                          name="textName"
                          label={t(
                            "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.NAME"
                          )}
                          className="w-100"
                        />
                      </div>
                      <div className="d-flex justify-content-end">
                        <$Button
                          size="small"
                          className="mr-2"
                          htmlType="submit"
                          type="primary"
                          onClick={handleSubmit}
                        >
                          {t("US.COMMON:COMMON.SAVE")}
                        </$Button>
                        <$Button
                          size="small"
                          onClick={() => setSaveAsVisible(false)}
                        >
                          {t("US.COMMON:COMMON.CANCEL")}
                        </$Button>
                      </div>
                    </div>
                  }
                  destroyTooltipOnHide
                  open={saveAsVisible}
                >
                  <$Button
                    onClick={() => setSaveAsVisible(true)}
                    className="mx-2"
                    disabled={content.id == ""}
                  >
                    {t("US.COLLECTION.COMMON:COMMON.SAVE_AS")}
                  </$Button>
                </$Popover>
                <$Button onClick={() => onClose && onClose()}>
                  {t("COMMON.CANCEL")}
                </$Button>
              </div>
            </div>
          </>
        )}
      </Formik>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { textEditor, editorHome, templatesHistory } = state;
  const { drawer } = textEditor;
  const { texts } = editorHome;
  const { templates } = templatesHistory;
  return {
    drawer,
    texts,
    templates,
  };
};

const { textData } = Actions.textEditor;

const mapDispatchToProps = {
  saveText: textData.update,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(TextEditor);
